/**
 * LanguageSwitcher — selector de idioma para o UserMenu/Topbar.
 *
 * Persiste a escolha em localStorage ("priceshow_lang") e atualiza o
 * i18next em runtime sem reload de página.
 *
 * Uso: inserido dentro do DropdownMenu do UserMenu, como grupo separado.
 */

import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";

import {
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuItem,
} from "@/components/ui/DropdownMenu";
import { I18N_STORAGE_KEY, SUPPORTED_LANGUAGES, type SupportedLanguage } from "@/i18n";

export function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as SupportedLanguage;

  function handleChange(code: SupportedLanguage) {
    try {
      localStorage.setItem(I18N_STORAGE_KEY, code);
    } catch {
      // localStorage bloqueado — ignora, mas aplica em memória
    }
    void i18n.changeLanguage(code);
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="flex items-center gap-2">
        <Globe className="h-4 w-4 shrink-0 text-[var(--color-muted-foreground)]" />
        {t("language.select")}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        {SUPPORTED_LANGUAGES.map(({ code, label }) => (
          <DropdownMenuItem
            key={code}
            onSelect={() => handleChange(code)}
            data-testid={`lang-option-${code}`}
            className={
              currentLang === code
                ? "font-semibold text-[var(--color-primary)]"
                : undefined
            }
          >
            {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
